/**
 * Full-flow session hydration policy (strategy + preferences).
 *
 * Product rules:
 * - First incomplete status → wipe once
 * - COMPLETED | SUBMITTED + empty session → seed from server; remember done
 * - COMPLETED | SUBMITTED + local committed map → skip (session owns answers; GET can lag POST)
 * - Incomplete after done → ignore (no wipe, no empty hydrate)
 * - Incomplete after wipe, still never done → keep local answers (no wipe)
 *
 * Belongs here: pure plan from status + wipe/seed progress.
 * Does not belong here: React, dispatch, payload parsing, or edit-route Prepare*Edit.
 */
import {
  isQuestionnaireStatusDone,
  type QuestionnaireStatus
} from './types/questionnaire.types'

/** What wipe/seed steps we have already taken this provider lifetime. */
export type FullFlowHydrationProgress = {
  /** True after the one-shot wipe for the first incomplete status. */
  hasWipedForIncomplete: boolean
  /** True after we have seen COMPLETED/SUBMITTED (seed may still be waiting on payload). */
  hasSeenDone: boolean
}

export const INITIAL_FULL_FLOW_HYDRATION_PROGRESS: FullFlowHydrationProgress = {
  hasWipedForIncomplete: false,
  hasSeenDone: false
}

export type FullFlowHydrationAction = 'wipe' | 'seed' | 'skip'

export type FullFlowHydrationPlan = {
  action: FullFlowHydrationAction
  hydrationProgress: FullFlowHydrationProgress
  /** Full-flow always parks cursor at the start for now. */
  resetCursorToStart: true
}

/**
 * Decide wipe / seed / skip for FullFlow hydrate.
 * Edit intents do not use this — Prepare*Edit owns those paths.
 *
 * @param hasLocalCommittedAnswers — session already owns a committed map (do not seed over it)
 */
export function getFullFlowHydrationPlan(
  status: QuestionnaireStatus | null | undefined,
  hydrationProgress: FullFlowHydrationProgress,
  hasLocalCommittedAnswers: boolean
): FullFlowHydrationPlan {
  if (isQuestionnaireStatusDone(status)) {
    if (hasLocalCommittedAnswers) {
      return {
        action: 'skip',
        hydrationProgress: { ...hydrationProgress, hasSeenDone: true },
        resetCursorToStart: true
      }
    }
    return {
      action: 'seed',
      hydrationProgress: { ...hydrationProgress, hasSeenDone: true },
      resetCursorToStart: true
    }
  }

  // Incomplete (or missing) after we already saw done → ignore regress
  if (hydrationProgress.hasSeenDone) {
    return { action: 'skip', hydrationProgress, resetCursorToStart: true }
  }

  // First incomplete → wipe once
  if (!hydrationProgress.hasWipedForIncomplete) {
    return {
      action: 'wipe',
      hydrationProgress: { ...hydrationProgress, hasWipedForIncomplete: true },
      resetCursorToStart: true
    }
  }

  // Already wiped, still incomplete → keep local answers
  return { action: 'skip', hydrationProgress, resetCursorToStart: true }
}

/** COMPLETED and SUBMITTED are one applied “done” for lastAppliedStatus. */
export function isSameAppliedHydrationStatus(
  previousStatus: QuestionnaireStatus | null | undefined,
  nextStatus: QuestionnaireStatus | null | undefined
): boolean {
  if (previousStatus === nextStatus) return true
  return isQuestionnaireStatusDone(previousStatus) && isQuestionnaireStatusDone(nextStatus)
}
